import { cn } from '@/lib/utils.ts';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { StatusTone } from '@/components/ui/statusTone.ts';
import { pluralize } from '@/utils/pluralize.ts';
import { BankTransactionImportResultDto } from '@/features/dashboard/rawBankTransactions/types/BankTransactionImportResultDto.ts';
import {
  IMPORT_STATUS_ICON,
  IMPORT_STATUS_LABELS,
  IMPORT_STATUS_TONE,
} from '@/features/dashboard/rawBankTransactions/types/ImportStatus.utils.ts';

interface ImportResultBannerProps {
  result: BankTransactionImportResultDto;
  /** Nazwę pliku zna tylko okno importu — DTO wyniku jej nie niesie. */
  fileName?: string;
}

interface CounterTile {
  label: string;
  value: number;
  tone: StatusTone;
}

/**
 * Backend wysyła szczegóły błędów jednym napisem. Rozbicie na linie pozwala
 * pokazać je listą; komunikaty bywają po polsku, bywają wyjątkiem Javy —
 * w obu przypadkach idą drobnym tekstem, nigdy jako nagłówek.
 */
const errorLines = (errorDetails: string | null): string[] =>
  (errorDetails ?? '')
    .split(/[\r\n]+|;\s*/)
    .map((line) => line.trim())
    .filter(Boolean);

const ImportResultBanner = ({ result, fileName }: ImportResultBannerProps) => {
  const StatusIcon = IMPORT_STATUS_ICON[result.status];
  const statusTone = IMPORT_STATUS_TONE[result.status];

  const counters: CounterTile[] = [
    {
      label: 'Zaimportowane',
      value: result.importedCount,
      tone: 'success',
    },
    { label: 'Dopasowane', value: result.matchedCount, tone: 'info' },
    { label: 'Duplikaty', value: result.duplicateCount, tone: 'neutral' },
    { label: 'Błędy', value: result.errorCount, tone: 'error' },
  ];

  const lines = errorLines(result.errorDetails);

  return (
    <div className="flex flex-col gap-4 rounded-md border border-neutral-200 bg-basic-white p-4">
      <div className="flex flex-col gap-1">
        <StatusDot
          className="text-sm font-medium text-neutral-800"
          tone={statusTone}
          icon={StatusIcon}
          label={IMPORT_STATUS_LABELS[result.status]}
        />
        <p className="text-xs text-neutral-500">
          {fileName ? `${fileName} · ` : ''}
          łącznie{' '}
          {pluralize(
            result.totalTransactions,
            'transakcja',
            'transakcje',
            'transakcji',
          )}
        </p>
      </div>

      {/* Dwie kolumny, nie cztery: „Zaimportowane” jest jednym słowem szerszym
          niż ćwiartka modalu i urywało się wielokropkiem. */}
      <div className="grid grid-cols-2 gap-2">
        {counters.map((counter) => (
          <div
            key={counter.label}
            className="flex flex-col gap-1 rounded-md border border-neutral-200 px-3 py-2"
          >
            <span
              className={cn(
                'text-lg font-semibold leading-none text-neutral-800',
                // Zero to brak zdarzenia, nie alarm — nawet w kolumnie błędów.
                counter.value === 0 && 'text-neutral-300',
              )}
            >
              {counter.value}
            </span>
            <StatusDot
              className="text-xs text-neutral-600"
              tone={counter.value === 0 ? 'neutral' : counter.tone}
              label={counter.label}
            />
          </div>
        ))}
      </div>

      {lines.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-neutral-700">Szczegóły</p>
          <ul className="flex flex-col gap-1">
            {lines.map((line, index) => (
              <li
                key={`${index}-${line}`}
                className="break-words text-xs leading-[1.5] text-neutral-600"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ImportResultBanner;
